import React, { useState, useRef } from "react"
import { Upload, AlertCircle, Loader2 } from "lucide-react"

interface FileUploaderProps {
  onFileUpload: (file: File) => void
  isProcessing: boolean
}

const FileUploader: React.FC<FileUploaderProps> = ({
  onFileUpload,
  isProcessing,
}) => {
  const [isDragging, setIsDragging] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(true)
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragging(false)

    if (isProcessing) return

    const files = e.dataTransfer.files
    validateAndUploadFile(files[0])
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndUploadFile(e.target.files[0])
    }
  }

  const validateAndUploadFile = (file: File) => {
    setError(null)

    const allowedTypes = [".pdf", ".txt"]
    const fileExtension = file.name
      .toLowerCase()
      .substring(file.name.lastIndexOf("."))

    if (!allowedTypes.includes(fileExtension)) {
      setError("Please upload a PDF or TXT file")
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError("File size must be less than 5MB")
      return
    }

    onFileUpload(file)
  }

  const handleButtonClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div className="w-full">
      <div
        className={`
          border-2 border-dashed rounded-lg p-1 
          flex flex-col items-center justify-center
          transition-all duration-300 ease-in-out
          ${isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"}
          ${isProcessing ? "opacity-70 pointer-events-none" : ""}
        `}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {isProcessing ? (
          <Loader2 className="w-12 h-12 text-blue-500 animate-spin mb-4" />
        ) : (
          <Upload className="w-12 h-12 text-blue-500 mb-4" />
        )}

        <h3 className="text-lg font-medium text-gray-700 mb-2">
          {isProcessing ? "Uploading and Parsing ..." : "Drag and drop your resume here"}
        </h3>
        <p className="text-sm text-gray-500 mb-4">or click to browse</p>
        <button
          onClick={handleButtonClick}
          disabled={isProcessing}
          className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 
                     transition-colors duration-300 disabled:bg-blue-500"
        >
          Select File
        </button>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf"
          className="hidden"
          disabled={isProcessing}
        />
        <p className="text-xs text-gray-400 mt-4">
          Supported formats: PDF, TXT (max 5MB)
        </p>

        {error && (
          <div className="mt-4 flex items-center text-red-500 gap-1">
            <AlertCircle className="w-4 h-4" />
            <p className="text-sm">{error}</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default FileUploader
